import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { AccountService } from 'app/core';
import { FieldDef, FieldDefType, QueryConfig, SavedFilter } from 'app/core/models';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project, Property } from 'app/modules/projects/projects.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { RelatedToType } from 'app/shared/enums';
import UrlUtils from 'app/shared/Utils/url.utils';
import cloneDeep from 'lodash-es/cloneDeep';
import filter from 'lodash-es/filter';
import moment from 'moment';
import { noop, Observable, of, finalize, switchMap, tap } from 'rxjs';
import { debounceTime, filter as rxFilter, map } from 'rxjs/operators';
import { ConfirmService } from '../dialog/confirm/confirm.service';
import { CompaniesService } from 'app/modules/companies/companies.service';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { plural } from 'pluralize';
import { CustomObjectsService } from 'app/shared/modules/custom-objects/services/custom-objects.service';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';

import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { MatLegacySlideToggle as MatSlideToggle } from '@angular/material/legacy-slide-toggle';
import { NewFilterField } from '../../../models/new-filter-field';
import { SavedFilters } from 'app/state/saved-filters/action';

@Component({
  selector: 'list-filter',
  templateUrl: './list-filter.component.html',
  styleUrls: ['./list-filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListFilterComponent implements OnInit {
  @Output() filterSearchInput = new EventEmitter<string>();
  @Output() projectFilters = new EventEmitter<any>();
  @Output() filterChanged = new EventEmitter<{ filters: any[]; options: any; isFilterValid: boolean }>();
  @Output() onClearFilters = new EventEmitter();
  @Output() autocompleteSearch = new EventEmitter<any>();
  // Use it to notify parent component about filter rows validity.
  @Output() filterRowsValidityChange = new EventEmitter<boolean>();

  @Input() hideSaveButton: boolean = false;
  @Input() hideFilterName: boolean = false;
  @Input() label: string;
  @Input() saveOnInputQuery: boolean = false;
  @Input() objectType = RelatedToType.Project;
  @Input() placeholder: string = 'Search...';
  @Input() listStyle: string;
  @Input() inData: any = {};
  @Input() promptRemove = true;
  @Input() fieldsFilterFn?: (fields: FieldDef[]) => FieldDef[];
  @Input() allowRelatedObjectFields = false;
  // Pass true in the parent-component if 'include Children' toggle slider is required.
  @Input() allowIncludeChildren = false;
  @Input() readonly = false;

  @Input() set onlyList(value: boolean) {
    this._onlyList = value;
  }

  @Input() set relatedToType(value: RelatedToType) {
    this._relatedToType = value;

    if (this.data) {
      this.refreshFieldList(true);
    }
  }

  @Input() set savedFilter(savedFilter: SavedFilter) {
    if (savedFilter === null) {
      this._savedFilter = { name: '', id: null };
      this.searchInputControl.setValue('', { emitEvent: false });
    } else {
      this._savedFilter = savedFilter;
      if (savedFilter?.filters) {
        const filters = JSON.parse(savedFilter.filters);
        if (filters['q']) {
          let queryValue = String(filters['q']);
          this.searchInputControl.setValue(queryValue);
          this.queryFilters = { ...filters, q: queryValue };
        } else {
          this.searchInputControl.setValue('', { emitEvent: false });
          this.queryFilters = { ...filters, q: '' };
        }
      }
    }
  }

  @Input() set queryFilters(filters: Partial<QueryConfig.QueryFilters>) {
    if (!this.onlyList) {
      if (!filters?.q) {
        this.searchInputControl.setValue('', { emitEvent: false });
      } else {
        this.searchInputControl.setValue(filters.q);
      }
      if (!filters?.includeChildren && this.includeChildrenToggle) {
        this.includeChildrenToggle.checked = false;
      }
    }
    this.filterOptions = filters?.filterOptions;
    this._queryFilters = filters;
    this.populateFilters(filters);
  }

  @ViewChild('filters') filters: ElementRef;
  @ViewChild('filterPanel') private _filterPanel: TemplateRef<any>;
  @ViewChild('filterPanelOrigin') private _filterPanelOrigin: MatButton;
  @ViewChild('includeChildrenToggle') includeChildrenToggle!: MatSlideToggle;

  isFilterRowsValid: boolean;

  get onlyList(): boolean {
    return this._onlyList;
  }

  get savedFilter(): SavedFilter {
    return this._savedFilter;
  }

  get queryFilters(): Partial<QueryConfig.QueryFilters> {
    return this._queryFilters;
  }

  // Check if the filter can be run
  get canRunFilter(): boolean {
    return this.canRunOrSaveFilter && this.canClearFilters;
  }

  // Check if the filter can be saved
  get canSaveFilter(): boolean {
    // If the filter name is not hidden...
    if (!this.hideFilterName) {
      // ...then the filter can be saved if it can be run and the saved filter name exists.
      return this.canRunOrSaveFilter && this.savedFilterNameExists;
    }

    return this.canRunOrSaveFilter;
  }

  // Check if the filter can be run or saved
  get canRunOrSaveFilter(): boolean {
    return this.hasEmptyFilterRow && this.isFilterRowsValid;
  }

  // Check if the filter rows or search query exists
  get canClearFilters(): boolean {
    return (this.filterRows.length > 0 || this.searchQueryExists) && !this.hideSaveButton;
  }

  // Check if any filter row is in default/empty state
  get hasEmptyFilterRow(): boolean {
    const hasEmptyFilterRow = this.filterRows.some(
      (filter) =>
        !filter.id && !filter.field && !filter.type && !filter.filter && !filter.value && !filter.alternate_value,
    );

    // Return false if there's an empty filter row
    return !hasEmptyFilterRow;
  }

  // Check if the saved filter name exists
  get savedFilterNameExists(): boolean {
    return Boolean(this.savedFilter?.name);
  }

  // Check if the search query exists
  get searchQueryExists(): boolean {
    return Boolean(this.queryFilters?.q);
  }

  // Check if the include children toggle should be disabled
  get disableIncludeChildrenToggle(): boolean {
    return !this.filterRows.length && !this.searchInputControl.value;
  }

  private _relatedToType: RelatedToType;
  private _savedFilter: SavedFilter;
  private _onlyList = false;
  private _queryFilters: Partial<QueryConfig.QueryFilters>;
  private _filterPanelOverlayRef: OverlayRef;
  private _filterPanelTemplatePortal: TemplatePortal;
  private _accountId: string;
  private validTypes: FieldDefType[] = [
    'number',
    'decimal',
    'percent',
    'date',
    'datetime',
    'money',
    'text',
    'year',
    'checkbox',
    'select',
    'record',
    'typeahead',
  ];

  removable = true;
  searchInputControl: UntypedFormControl;
  filterRows = [];
  searchBarDimensions = {
    defaultWidth: 215,
    singleExpandedWidth: 425,
    doubleExpandedWidth: 550,
    maxExpandedWidth: 600,
    width: 215,
  };
  newFilterFields: NewFilterField[] = [];
  data: Data & { fieldDefs: FieldDef[] };
  fieldDefs: FieldDef[] = [];
  customFieldDefs: FieldDef[] = [];
  standardFieldDefs: FieldDef[] = [];
  objectFieldDefs: any[] = [];
  recordResults: any;
  minRecordSearchStringLen = 3;
  autocompleteSearchOpts$ = {};
  filterOptions: {};
  typeaheadResults: any[];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _accountService: AccountService,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _overlay: Overlay,
    private readonly _httpClient: HttpClient,
    private readonly projectsService: ProjectsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly confirmService: ConfirmService,
    private readonly propertiesService: PropertiesService,
    private readonly companiesService: CompaniesService,
    private readonly contactsService: ContactsService,
    private readonly customObjectsService: CustomObjectsService,
    private readonly store: Store,
  ) {
    this.searchInputControl = new UntypedFormControl();
    this._accountId = this.store.selectSnapshot(AccountState.getAccountId);
  }

  ngOnInit(): void {
    this._activatedRoute.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: Data & { fieldDefs: FieldDef[] }) => {
        this.data = data;
        if (Object.keys(this.data).length <= 0) {
          this.data = this.inData;
        }

        this.fieldDefs = this.fetchFieldDefs();
        this.refreshFieldList();
        this.populateFilters(this.queryFilters);
      });

    //[sc-11674]: permission to prevent save filters on dynamic pipelines
    this.removable = this.isAllowedToRemove();

    this.searchInputControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
      .subscribe((data) => {
        if (this.saveOnInputQuery) {
          if (this.queryFilters?.q === undefined || this.queryFilters?.q != data) this.updateQueryFilter();
        } else {
          if (this.queryFilters?.q === undefined) {
            this.updateQueryFilter();
          }

          if (this.queryFilters?.q != data) {
            if (
              (Object.keys(this.queryFilters).length === 0 && data !== '') ||
              Object.keys(this.queryFilters).length >= 1
            ) {
              this.updateQueryFilter();
            }
          }
        }
      });
  }

  private fetchFieldDefs(): FieldDef[] {
    return this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(this._relatedToType ?? this.objectType, {
        allowedFieldsOnly: true,
        addNestedFields: this.allowRelatedObjectFields,
      }),
    );
  }

  public toggleIncludeChildren(checked: boolean): void {
    this.updateQueryFilter(checked, false);
  }

  private approvedType(field: FieldDef): boolean {
    return this.validTypes.includes(field.type);
  }

  private getOptions(field: FieldDef): any[] {
    if (field.meta?.shapeShifted) {
      return field.meta.options.map(({ id, name }) => ({ id, name }));
    }

    if (field.meta.option_name) {
      if (!this.data.options[field.meta.option_name]) {
        return [];
      }

      let options = this.data.options[field.meta.option_name].filter((option) => !option.hidden);
      if (field.meta.option_name == 'pipelines') {
        options = options.filter((pipeline) => pipeline?.pipeline_type != 'dynamic');
      }

      return options;
    } else if (field.meta.options) {
      return field.meta.options.map((option) => ({ id: option, name: option }));
    }
  }

  private getCustomOptions(field: FieldDef): any[] {
    return !field.meta.option_name
      ? field.meta.options
      : this.data?.options
        ? this.data?.options[plural(field.meta.record_type.toLowerCase())] || []
        : [];
  }

  private addStandardField(field: FieldDef): void {
    this.newFilterFields.push({
      id: field.name,
      name: field.label,
      type: field.type,
      options: [],
      custom: false,
      operators: field.meta.filtering.operators,
      functions: field.meta.filtering?.functions,
      values: field.meta.filtering?.values,
      related_to_type: field.related_to_type,
      exclude_in_nested_filter: field.meta?.filtering?.exclude_in_nested_filter ?? false,
      exclude_in_nested_fields: field.meta?.exclude_in_nested_fields ?? false,
      ...this.generateGroupingProps(field),
    });
  }

  private addRecordCustomField(field: FieldDef): void {
    const options = this.getCustomOptions(field);
    this.newFilterFields.push({
      id: field.name,
      name: field.label,
      type: options.length > 0 ? 'multiple' : 'autocomplete',
      options,
      custom: true,
      record_type: field.meta.record_type,
      operators: field.meta.filtering.operators,
      field_id: field.id,
      functions: field.meta.filtering?.functions,
      values: field.meta.filtering?.values,
      related_to_type: field.related_to_type,
      exclude_in_nested_filter: field.meta?.filtering?.exclude_in_nested_filter ?? false,
      exclude_in_nested_fields: field.meta?.exclude_in_nested_fields ?? false,
      ...this.generateGroupingProps(field),
    });
  }

  private addCustomField(field: FieldDef): void {
    this.newFilterFields.push({
      id: field.name,
      name: field.label,
      type: field.type,
      options: [],
      custom: true,
      operators: field.meta.filtering.operators,
      field_id: field.id,
      functions: field.meta.filtering?.functions,
      values: field.meta.filtering?.values,
      related_to_type: field.related_to_type,
      exclude_in_nested_filter: field.meta?.filtering?.exclude_in_nested_filter ?? false,
      exclude_in_nested_fields: field.meta?.exclude_in_nested_fields ?? false,
      ...this.generateGroupingProps(field),
    });
  }

  private addDefaultSelectField(field: FieldDef): void {
    let options = this.getOptions(field);

    this.newFilterFields.push({
      id: field.name,
      name: field.label,
      type: field.name === 'project_id' || field.name === 'property_id' ? 'autocomplete' : 'multiple',
      options: options,
      custom: false,
      operators: field.meta.filtering.operators,
      values: field.meta.filtering?.values,
      related_to_type: field.related_to_type,
      exclude_in_nested_filter: field.meta?.filtering?.exclude_in_nested_filter ?? false,
      exclude_in_nested_fields: field.meta?.exclude_in_nested_fields ?? false,
      ...this.generateGroupingProps(field),
    });
  }

  private addCustomSelectField(field: FieldDef): void {
    let options = this.getCustomOptions(field);
    this.newFilterFields.push({
      id: field.name,
      name: field.label,
      type: 'select',
      options: options,
      custom: true,
      operators: field.meta.filtering.operators,
      values: field.meta.filtering?.values,
      field_id: field.id,
      related_to_type: field.related_to_type,
      exclude_in_nested_filter: field.meta?.filtering?.exclude_in_nested_filter ?? false,
      exclude_in_nested_fields: field.meta?.exclude_in_nested_fields ?? false,
      ...this.generateGroupingProps(field),
    });
  }

  private generateGroupingProps(field: FieldDef): { group_by: string; group_label: string } | {} {
    if (this.allowRelatedObjectFields) {
      // it relies on the preprocessing of nested fields performed by the utility function formatRelatedFieldToNested
      // used in the selector getFieldsByObjectName
      const filterFieldGroupKey = field.name.includes('.') ? field.name?.split('.')?.[0] : field.related_to_type;
      const groupLabel = this.fieldDefs.find((x) => x.name === filterFieldGroupKey)?.label || field.related_to_type;

      return {
        group_by: filterFieldGroupKey,
        group_label: groupLabel,
      };
    }

    return {};
  }

  public openFiltersPanel(): void {
    this._filterPanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._filterPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withLockedPosition()
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
          },
        ]),
    });

    this._filterPanelOverlayRef.attachments().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noop);

    this._filterPanelTemplatePortal = new TemplatePortal(this._filterPanel, this._viewContainerRef);

    this._filterPanelOverlayRef.attach(this._filterPanelTemplatePortal);

    this._filterPanelOverlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this._closeFilterPanel());
  }

  private _closeFilterPanel(): void {
    if (this._filterPanelOverlayRef && this._filterPanelOverlayRef.hasAttached()) {
      this._filterPanelOverlayRef.detach();
    }

    if (this._filterPanelTemplatePortal && this._filterPanelTemplatePortal.isAttached) {
      this._filterPanelTemplatePortal.detach();
    }
  }

  private resizeSearchBar(): void {
    switch (this.filterRows.length) {
      case 0:
        this.searchBarDimensions.width = this.searchBarDimensions.defaultWidth;
        break;
      case 1:
        this.searchBarDimensions.width = this.searchBarDimensions.singleExpandedWidth;
        break;
      case 2:
        this.searchBarDimensions.width = this.searchBarDimensions.doubleExpandedWidth;
        break;
      default:
        this.searchBarDimensions.width = this.searchBarDimensions.maxExpandedWidth;
    }
  }

  onFilterRowsValidityChange(isValid: boolean) {
    this.isFilterRowsValid = isValid;
    this.filterRowsValidityChange.emit(this.isFilterRowsValid);
  }

  public addFilterRow(): void {
    if (this.savedFilter === null) this.savedFilter = { name: '', id: null };
    this.filterRows = [...this.filterRows, { id: '', field: '', type: '', filter: '', value: '', alternate_value: '' }];
    this.resizeSearchBar();
  }

  private updateQueryFilter(includeChildren: boolean = false, emitFilter: boolean = true): void {
    // Initialize empty filters object
    let filters = {};
    // Create deep copy of filterRows to avoid modifying original
    const tmpFilters = cloneDeep(this.filterRows);

    // Process each non-empty filter row
    tmpFilters
      .filter((x) => x.id?.length)
      .forEach((filter) => {
        // Handle array values
        if (Array.isArray(filter.value)) {
          if (filter.value.length === 0) filter.value = '';
        }
        // Handle record type filters
        else if (filter.type === 'record') {
          if (filter.alternate_value) {
            filter.value = [];
            filter.value.push(filter.alternate_value);
          } else return;
        }

        // Initialize fjson array if doesn't exist
        if (!filters['fjson']) filters['fjson'] = [];

        // Add filter to fjson array in format: [id, operator, value]
        if (['date', 'datetime'].includes(filter.type) && !filter.value) {
          return;
        }

        filters['fjson'].push([filter.id, filter.filter, filter.value]);
      });

    // Stringify fjson array if it exists
    if (filters['fjson']) filters['fjson'] = JSON.stringify(filters['fjson']);

    // Add search query value
    filters['q'] = this.searchInputControl.value ? this.searchInputControl.value : '';

    // Add filter options if they exist
    if (this.filterOptions) filters['filterOptions'] = this.filterOptions;

    // Update queryFilters with new filters, optionally including children
    this.queryFilters = includeChildren ? { ...filters, includeChildren } : filters;

    // Emit filters to parent component if emitFilter is true
    if (emitFilter) this.projectFilters.emit(this.queryFilters);
  }

  public applyFilter(): void {
    this.updateQueryFilter(this.queryFilters?.includeChildren);
    UrlUtils.saveToLocalStorage(`${this.objectType}_queryFilters`, this.queryFilters, true);
    this._closeFilterPanel();
  }

  public applyFilterAndSave(): void {
    this.applyFilter();
    this.saveFilter(this.queryFilters);
  }

  public filterNameChange(event: string): void {
    this.savedFilter.name = event || '';
  }

  public removeFilter(index: number): void {
    const filter = this.filterRows[index];

    const isEmptyFilter =
      !filter.id && !filter.field && !filter.type && !filter.filter && !filter.value && !filter.alternate_value;

    if (this.isAllowedToRemove()) {
      this.filterRows.splice(index, 1);
      this.resizeSearchBar();
      if (!isEmptyFilter) {
        this.updateQueryFilter();
        this.filterUpdated();
      }
    }
  }

  public filterUpdated(): void {
    this.triggerfilterChanged({ filters: this.filterRows, options: this.filterOptions });
  }

  /**
   * Emit filterChanged event with filters, options and isFilterValid status after being checked
   * @param data filters and options.
   */
  private triggerfilterChanged(data: { filters: any[]; options: any }): void {
    const isFilterValid = this.isFilterRowsValid;
    this.filterChanged.emit({
      filters: data.filters,
      options: data.options,
      isFilterValid,
    });
  }

  private isAllowedToRemove(): boolean {
    return this._relatedToType === RelatedToType.Contact ? true : !this.hideSaveButton;
  }

  private clearFilter(savedFilterDeleted: boolean = false): void {
    this.filterRows = [];
    this.queryFilters = {};
    this.searchInputControl.reset('', { emitEvent: false });
    this.resizeSearchBar();
    this._closeFilterPanel();
    if (this.savedFilter?.id && !savedFilterDeleted && !this.hideSaveButton) this.saveFilter(this.queryFilters);
  }

  public deleteFilters(): void {
    this.confirmService
      .openConfirmationModal({
        message: `Are you sure you want to delete <strong>${this.savedFilter.name}</strong> filter?`,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        rxFilter((result: boolean) => result),
        switchMap(() => this._accountService.deleteFilter(this.savedFilter.id)),
      )
      .subscribe(() => {
        this.clearFilter(true);
        const url = this._router.url.split('?')[0];
        const queryParams = { filterId: this._router.url.endsWith('reset') ? 'reset' : 'clear' };
        this._router.navigate([url], { queryParams });
        this.store.dispatch([SavedFilters.Get]);
      });
  }

  public filterFieldSelected(eventArgs: any): void {
    const { event, filter } = eventArgs;
    filter.id = event.value.id;
    filter.field = event.value;
    filter.type = event.value.type;
    filter.filter = event.value.operators[0];

    if (event.value.options) filter.options = event.value.options;
    if (event.value.type === 'autocomplete') filter.value = [];

    this.filterUpdated();
  }

  public filterValueChange(eventArgs: any): void {
    const { filter, event, options } = eventArgs;

    if (Object.keys(options || {}).length) {
      this.filterOptions = options;
    }

    if (
      filter.type === 'string' ||
      filter.type === 'number' ||
      filter.type === 'decimal' ||
      filter.type === 'percent' ||
      filter.type === 'money' ||
      filter.type === 'text' ||
      filter.type === 'year'
    ) {
      filter.value = event;
    } else if (filter.type === 'date' || filter.type === 'datetime') {
      if (filter.filter == 'is') {
        filter.value = event.value;
      } else {
        filter.value = event?.toString() ? moment(event.toString()).format('YYYY-MM-DD') : '';
      }
    } else if (filter.type === 'multiple' || filter.type === 'select' || filter.type === 'checkbox') {
      filter.value = event.value;
    } else if (filter.type === 'autocomplete') {
      if (event.value.checked === false) {
        filter.value = filter.value.filter((x) => +x[0] !== +event.value[filter.id.replace('_id', '')][0]);
      } else if (filter.value === '') {
        filter.value = [event.value[filter.id.replace('_id', '')]];
      } else {
        filter.value.push(event.value[filter.id.replace('_id', '')]);
      }
    } else if (filter.type === 'record') {
      filter.value = event?.option?._element?.nativeElement?.innerText;
      filter.alternate_value = event?.option?.value;
    } else if (filter.type === 'typeahead') {
      filter.value = event.option.value;
    }

    this.filterUpdated();
  }

  public populateFilters(filters: Partial<QueryConfig.QueryFilters>): void {
    this.filterRows = [];
    if (filters && this.newFilterFields.length > 0) {
      Object.keys(filters).forEach((x) => {
        let field = this.newFilterFields.find((filter) => {
          return filter.id === x;
        });
        if (x && !field && x.includes('Id')) {
          let x2 = x
            .split(/(?=[A-Z])/)
            .join('_')
            .toLowerCase()
            .replace('_in', '');
          field = this.newFilterFields.find((filter) => {
            return filter.id === x2;
          });
        }
        if (field) {
          if (field?.type === 'multiple') {
            let valueArray = Array.from(filters[x]);
            if (valueArray.length > 0) {
              this.filterRows.push({
                id: field.id,
                field: field,
                type: field.type,
                filter: 'in',
                value: filters[x].map((i) => Number(i)),
                options: field.options,
              });
            } else filters[x] = '';
          } else if (filters[x]) {
            this.filterRows.push({
              id: field.id,
              field: field,
              type: field.type,
              filter: 'eq',
              value: filters[x],
              options: field.options,
            });
          }
        }
      });

      if (filters['fjson']) {
        if (this.newFilterFields.length > 0) {
          const fjson = JSON.parse(filters['fjson']);
          for (const customFilter in fjson) {
            const field = this.newFilterFields.find((filter) => filter.id === fjson[customFilter][0]);

            if (!field) {
              continue;
            }

            if (field?.type === 'record') {
              this.filterRows.push({
                id: fjson[customFilter][0],
                field: field,
                type: field.type,
                filter: fjson[customFilter][1],
                value: this.getMultiRecordName(fjson[customFilter][2]) ?? '',
                alternate_value: fjson[customFilter][2],
                options: field.options,
              });
            } else {
              this.filterRows.push({
                id: fjson[customFilter][0],
                field: field,
                type: field.type,
                filter: fjson[customFilter][1],
                value: fjson[customFilter][2],
                options: field.options,
              });
            }
          }
        }
      }
    }
    this.resizeSearchBar();
  }

  private saveFilter(filters: Partial<QueryConfig.QueryFilters>): void {
    if (this.savedFilter && this.savedFilterNameExists) {
      this.savedFilter.filters = JSON.stringify(filters);

      const saveOrUpdate$ = this.savedFilter.id
        ? this._accountService.updateFilter(this.savedFilter)
        : this._accountService.saveFilter(this.savedFilter);

      saveOrUpdate$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((savedFilter) => {
            if (!this.savedFilter.id) {
              const url = this._router.url.split('?')[0];
              this._router.navigate([url], { queryParams: { filterId: savedFilter.id } });
            }
          }),
          finalize(() => this.store.dispatch([SavedFilters.Get])),
        )
        .subscribe(noop);
    }
  }

  public clear(): void {
    this.clearFilter();
    this.applyFilter();
    this.onClearFilters.emit();
  }

  public maxFieldName(name: string): string {
    if (name) {
      if (name.length > 12) return name.substring(0, 9) + '...';
      else return name;
    } else {
      ('');
    }
  }

  public autocompleteRecord(eventArgs: any): void {
    const { event, filter } = eventArgs;
    this.filterValueChange({ event, filter });
  }

  public autocompleteTypeahead(eventArgs: any): void {
    const { event, filter } = eventArgs;
    this.filterValueChange({ event, filter });
  }

  public onAutocompleteSearch(event: any): void {
    const dealPropertiesSearchOpts =
      this.data.module === 'REPORTS'
        ? {
            [RelatedToType.Project]: this.getSearchProjectObservable(event.value, event.filter),
            [RelatedToType.Property]: this.getSearchPropertyObservable(event.value, event.filter),
          }
        : {
            project_id: this.getSearchProjectObservable(event.value, event.filter),
            property_id: this.getSearchPropertyObservable(event.value, event.filter),
          };

    this.autocompleteSearchOpts$ = {
      ...dealPropertiesSearchOpts,
      [RelatedToType.Company]: this.getSearchCompanyObservable(event.value, event.filter),
      [RelatedToType.Contact]: this.getSearchContactObservable(event.value, event.filter),
      [RelatedToType.CustomObject]: this.getSearchCustomObjectObservable(event.value, event.filter),
    };
  }

  public onRecordSearchChange(eventArgs: any): void {
    const { trigger, value, filter } = eventArgs;
    if (filter.field?.field_id && value.length >= this.minRecordSearchStringLen) {
      this._httpClient
        .get<any>(`custom_fields/${filter.field.field_id}/search_records?name=${value}`)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: any) => {
          this.recordResults = response;
          trigger.openPanel();
        });
    } else {
      this.recordResults = [];
    }
  }

  public onTypeaheadSearchChange(eventArgs: { value: string; filter: any }): void {
    this._accountService
      .typeaheadSearch(eventArgs.value, eventArgs.filter.field.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.typeaheadResults = response;
        this.changeDetectorRef.detectChanges();
      });
  }

  private getMultiRecordName(record: any): string {
    if (Array.isArray(record) && record.length === 2 && typeof record[1] === 'string') {
      return record[1];
    }

    if (record[0]) {
      let result = record[0][0][1];
      if (result === undefined) result = record[0][1];

      return result;
    }
  }

  private refreshFieldList(relatedChanged = false): void {
    if (relatedChanged === true) {
      this.fieldDefs = this.fetchFieldDefs();
      this.handleRelatedObjectFields();
    }

    if (this.fieldsFilterFn) {
      this.fieldDefs = this.fieldsFilterFn(this.fieldDefs);
    }

    this.fieldDefs
      .filter((x) => x.meta?.shapeShifter)
      .forEach((x) => {
        x.type = x.meta.shapeShifter.type;
        x.meta = { ...x.meta.shapeShifter.meta, shapeShifted: true };
      });

    this.objectFieldDefs = [...this.fieldDefs];

    this.customFieldDefs = filter(this.objectFieldDefs, function (o) {
      return o.meta?.is_custom;
    });

    this.standardFieldDefs = filter(this.objectFieldDefs, function (o) {
      return !o.meta?.is_custom;
    });

    this.generateNewFilterFields();
  }

  private generateNewFilterFields(): void {
    this.newFilterFields = [];

    for (let standardField of this.standardFieldDefs) {
      if (this.approvedType(standardField)) {
        if (
          standardField.meta &&
          standardField.meta.filtering &&
          standardField.meta.filtering.operators &&
          standardField.type !== 'select'
        ) {
          this.addStandardField(standardField);
        }

        if (
          standardField.meta &&
          standardField.meta.filtering &&
          standardField.meta.filtering.operators &&
          standardField.type === 'select'
        ) {
          this.addDefaultSelectField(standardField);
        }
      }
    }

    for (let customField of this.customFieldDefs) {
      if (this.approvedType(customField) && customField.meta?.filtering?.operators) {
        if (customField.type === 'record') {
          this.addRecordCustomField(customField);
        } else if (customField.type !== 'select') {
          this.addCustomField(customField);
        } else if (customField.type === 'select') {
          this.addCustomSelectField(customField);
        }
      }
    }
  }

  private getSearchProjectObservable(value: string, filter: any): Observable<Project[]> {
    return this.projectsService
      .getProjectsNoPipeline(['id', 'name'], {}, 0, 20, 'name', 'asc', value, false, null, true)
      .pipe(
        map((response) => {
          let selectedItems = [];

          if ((!value || value === '') && filter?.value) {
            selectedItems = filter.value.map((i) => ({ id: i[0], name: i[1] }));
          }

          const autocompleteItems = [...(selectedItems as any[]), ...(response.projects as any[])].filter(
            (item, index, self) => index === self.findIndex((t) => t.id === item.id),
          );
          return autocompleteItems;
        }),
      );
  }

  private getSearchPropertyObservable(value: string, filter: any): Observable<Property[]> {
    return this.propertiesService.getProperties(['id', 'name', 'address_string'], {}, 0, 20, 'name', 'asc', value).pipe(
      map((response) => {
        let selectedItems = [];

        if ((!value || value === '') && filter?.value) {
          selectedItems = filter.value.map((i) => ({ id: i[0], name: i[1] }));
        }

        const autocompleteItems = [...(selectedItems as any[]), ...(response.properties as any[])].filter(
          (item, index, self) =>
            (item.name || item.address_string) && index === self.findIndex((t) => t.id === item.id),
        );
        return autocompleteItems;
      }),
    );
  }

  private getSearchCompanyObservable(value: string, filter: any): Observable<any[]> {
    return this.companiesService.search({ fjson: '', q: value }, 0, 20).pipe(
      map((response) => {
        let selectedItems = [];

        if ((!value || value === '') && filter?.value) {
          selectedItems = filter.value.map((i) => ({ id: i[0], name: i[1] }));
        }

        const autocompleteItems = [...(selectedItems as any[]), ...(response.results as any[])].filter(
          (item, index, self) =>
            (item.name || item.address_string) && index === self.findIndex((t) => t.id === item.id),
        );

        return autocompleteItems;
      }),
    );
  }

  private getSearchContactObservable(value: string, filter: any): Observable<any[]> {
    return this.contactsService.search({ fjson: '', q: value }, 0, 20).pipe(
      map((response) => {
        let selectedItems = [];

        if ((!value || value === '') && filter?.value) {
          selectedItems = filter.value.map((i) => ({ id: i[0], name: i[1] }));
        }

        const autocompleteItems = [...(selectedItems as any[]), ...(response.results as any[])].filter(
          (item, index, self) =>
            (item.name || item.address_string) && index === self.findIndex((t) => t.id === item.id),
        );

        return autocompleteItems;
      }),
    );
  }

  private getSearchCustomObjectObservable(value: string, filter): Observable<any> {
    const customObjects = this.store.selectSnapshot(ObjectsManagerState.get);
    const customField = customObjects?.find((obj) => obj.name === filter.field.record_type);
    if (!customField) return of([]);

    const searchField = customField.fields?.find((field) => field.id === customField.search_custom_field_id);
    return this.customObjectsService
      .getValues(+this._accountId, customField.id, undefined, undefined, 0, 20, 'name', 'asc', value)
      .pipe(
        map((response) => {
          return response.values.map((obj) => ({ id: obj.id, name: obj[searchField.name] }));
        }),
      );
  }

  private handleRelatedObjectFields(): void {
    if (this.allowRelatedObjectFields) {
      this.fieldDefs = [...this.fieldDefs];
    }
  }
}
